import { useEffect, useRef } from 'react'
import { Application } from 'pixi.js'
import { CoverPage } from './pages/CoverPage'
import { HallPage } from './pages/HallPage'
import { EraSelectPage } from './pages/EraSelectPage'
import { GameContainer } from './components/GameContainer'
import { AuthPanel } from './components/AuthPanel'
import { UpdatePrompt } from './components/UpdatePrompt'
import { useShangGameStore } from './stores/shangGameStore'
import { useNavigationStore } from './stores/navigationStore'
import { useVersionCheck } from './hooks/useVersionCheck'
import { initializeNewGame, printInitResult } from './services/gameInitService'
import './App.css'

function App() {
  const { currentPage, setCurrentPage } = useNavigationStore()
  const canvasRef = useRef<HTMLDivElement>(null)
  const appRef = useRef<Application | null>(null)

  // 版本检测和更新
  const versionCheck = useVersionCheck()

  // 初始化 PixiJS（只在进入游戏时）
  useEffect(() => {
    if (currentPage !== 'game' || !canvasRef.current || appRef.current) return

    const initPixi = async () => {
      const app = new Application()
      await app.init({
        width: canvasRef.current!.clientWidth,
        height: canvasRef.current!.clientHeight,
        backgroundColor: 0xf5f0e8,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
      })

      canvasRef.current!.appendChild(app.canvas)
      appRef.current = app

      // 响应式调整
      const resize = () => {
        if (canvasRef.current) {
          app.renderer.resize(
            canvasRef.current.clientWidth,
            canvasRef.current.clientHeight
          )
        }
      }
      window.addEventListener('resize', resize)

      return () => {
        window.removeEventListener('resize', resize)
        app.destroy()
      }
    }

    initPixi()
  }, [currentPage])

  // 处理从封面进入
  const handleEnterFromCover = () => {
    setCurrentPage('hall')
  }

  // 处理大厅导航
  const handleNewGame = () => {
    setCurrentPage('era-select')
  }

  const handleLoadGame = () => {
    setCurrentPage('load-game')
  }

  const handleProfile = () => {
    setCurrentPage('profile')
  }

  const handleHome = () => {
    setCurrentPage('cover')
  }

  // 处理开始游戏
  const handleStartGame = (scenarioId: string, factionId: string) => {
    console.log('开始游戏:', { scenarioId, factionId })

    try {
      // 使用新的游戏初始化服务
      const startDate = { year: -770, month: 1, day: 1 }
      const result = initializeNewGame(scenarioId, factionId, startDate)

      if (result.success) {
        printInitResult(result)
        console.log('游戏初始化成功，人物数量:', result.characterStats.totalCharacters)
      } else {
        console.error('游戏初始化失败:', result.errors)
      }

      // 初始化商朝游戏状态（兼容旧代码）
      useShangGameStore.getState().initializeGame(scenarioId, factionId)
      setCurrentPage('game')
    } catch (error) {
      console.error('游戏初始化出错:', error)
      alert('游戏初始化失败，请查看控制台错误信息')
    }
  }

  // 渲染当前页面
  const renderPage = () => {
    switch (currentPage) {
      case 'cover':
        return <CoverPage onEnter={handleEnterFromCover} />

      case 'hall':
        return (
          <HallPage
            onNewGame={handleNewGame}
            onLoadGame={handleLoadGame}
            onProfile={handleProfile}
            onHome={handleHome}
          />
        )

      case 'era-select':
        return (
          <EraSelectPage
            onBack={() => setCurrentPage('hall')}
            onStartGame={handleStartGame}
          />
        )

      case 'load-game':
        // TODO: 实现载入存档页面
        return (
          <div className="temp-page">
            <h1>载入存档</h1>
            <p>存档列表</p>
            <button onClick={() => setCurrentPage('hall')}>返回</button>
          </div>
        )

      case 'profile':
        // TODO: 实现君主档案页面
        return (
          <div className="temp-page">
            <h1>君主档案</h1>
            <p>个人信息、成就、统计</p>
            <button onClick={() => setCurrentPage('hall')}>返回</button>
          </div>
        )

      case 'game':
        return (
          <div className="app">
            <div className="game-canvas" ref={canvasRef} />
            <GameContainer />
          </div>
        )

      default:
        return <CoverPage onEnter={handleEnterFromCover} />
    }
  }

  return (
    <div className="app-wrapper">
      {renderPage()}
      <AuthPanel />
      <UpdatePrompt
        isOpen={versionCheck.showUpdatePrompt}
        currentVersion={versionCheck.versionInfo.currentVersion}
        latestVersion={versionCheck.versionInfo.latestVersion}
        onUpdate={versionCheck.performUpdate}
        onDismiss={() => versionCheck.setShowUpdatePrompt(false)}
      />
    </div>
  )
}

export default App
